import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { styled, Typography, Grid } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import TextField from 'components/FormElements/AppTextInput';
import GridContainer from 'components/GridContainer';
import PageContainer from 'components/PageContainer';
import DropzoneComponent from 'components/Dropzone';
import PageContent from 'components/PageContent';
import { DASHBOARD } from 'adminConstants/routes';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import { adminUser as rules } from 'further-ui/validations';
import Spacing from 'components/Spacing';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';

const StyledLink = styled(Button)(({ theme }) => ({
  padding: 0,
  fontSize: theme.fontSizes?.regular,
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const Profile: React.FC = () => {
  const { success, error } = useNotification();
  const { uploadFile } = useFileUpload();
  const [profilePicPreview, setProfilePicPreview] = useState<string>();
  const [profilePic, setProfilePic] = useState<string>();

  const {
    profile: { data, isLoading },
    updateProfile: { mutate, isPending },
    requestPasswordResetForSelf: {
      mutate: requestPasswordResetForSelf,
      isPending: isRequestPasswordResetPending,
    },
  } = useCurrentUser();

  useEffect(() => {
    if (!data) return;

    if (!data.profilePic.endsWith('default.jpg')) {
      setProfilePicPreview(data.profilePic);
      setProfilePic(data.profilePic);
    }
  }, [data]);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    shouldFocusError: true,
    resolver: zodResolver(rules.updateProfileInput),
    criteriaMode: 'all',
  });

  const onSubmit = async (formData) => {
    const diff = Object.keys(formData).reduce((acc, key) => {
      if (formData[key] !== data?.[key]) {
        //@ts-expect-error
        acc.push(key);
      }
      return acc;
    }, []);

    mutate(
      {
        ...formData,
        profilePic: profilePic?.includes('.com/')
          ? profilePic.split('.com/')[1]
          : profilePic,
        updates: diff,
      },
      {
        onError: () => {
          error('Error when trying to update the profile.');
        },
        onSuccess: () => {
          success('Profile updated successfully.');
        },
      },
    );
  };

  const handleProfileImage = async (file) => {
    if (!file?.[0]?.path) return;

    const { data, error: uploadError } = await uploadFile(
      file[0],
      UploadType.UserImages,
    );
    if (data) {
      setProfilePicPreview(file?.[0]?.preview);
      setProfilePic(data.filePath);
    } else {
      error(uploadError.message);
    }
  };

  return (
    <PageContainer
      heading="Profile"
      breadcrumbs={[
        { label: 'Dashboard', link: '/dashboard' },
        { label: 'Profile', link: '/', isActive: true },
      ]}
      loading={isLoading}
    >
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <GridContainer>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="firstName"
                defaultValue={data?.firstName}
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    label="First name"
                    variant="outlined"
                    required
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message?.toString()}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="lastName"
                defaultValue={data?.lastName}
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    label="Last name"
                    variant="outlined"
                    required
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message?.toString()}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="email"
                defaultValue={data?.email}
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    label="Email Address"
                    variant="outlined"
                    required
                    error={!!errors.email}
                    helperText={errors.email?.message?.toString()}
                    fullWidth
                    disabled
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Spacing size="sm">
                <TextField
                  label="Password"
                  variant="outlined"
                  value="********"
                  disabled
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <ButtonGroup align="left">
                  <StyledLink
                    variant="text"
                    onClick={() => requestPasswordResetForSelf()}
                    loading={isRequestPasswordResetPending}
                  >
                    Change password
                  </StyledLink>
                </ButtonGroup>
              </Spacing>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Typography component="h4" variant="inherit">
                Profile Image
              </Typography>
              <DropzoneComponent
                name="profilePic"
                defaultAcceptGroup="images"
                onDropFile={(files) => handleProfileImage(files)}
                onRemove={() => {
                  setProfilePicPreview(undefined);
                  setProfilePic(undefined);
                }}
                files={
                  profilePicPreview
                    ? [{ preview: profilePicPreview, fileName: '' }]
                    : []
                }
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 12,
              }}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isPending}
              >
                Update
              </Button>
              <Link to={DASHBOARD}>
                <CancelButton variant="contained">Cancel</CancelButton>
              </Link>
            </Grid>
          </GridContainer>
        </form>
      </PageContent>
    </PageContainer>
  );
};

export default Profile;
